import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Alert, Button, FormProgress, Icon, Spinner } from '@innovationnorway/cl-react';
import AboutPage from './AboutPage';
import TargetPage from './TargetPage';
import DeliveryPage from './DeliveryPage';
import ContactInfoPage from './ContactInfoPage';
import EEAPage from './EEAPage';
import SummaryPage from './SummaryPage';
import { toast } from 'react-toastify';
import { useCluster } from 'src/features/cluster';
import {
  ClusterDto,
  ClusterUserDto,
  EmailInterval,
  OneWayInServiceDto,
  OneWayInServiceUpsertDto,
  PublicationState,
  ServiceTemplateDto,
} from 'src/api/v2';
import useOneWayInServices from 'src/features/one-way-in/hooks/use-one-way-in-services';
import { toastPromise } from 'src/utils/toast';
import { useCurrentClusterUser } from 'src/hooks/use-current-cluster-user';
import { useNavigate, useParams } from 'react-router-dom';
import useOneWayInService from '../hooks/use-service';
import { FieldErrors, UseFormSetError } from 'react-hook-form';
import { SubFormProps } from '../hooks/use-service-sub-form';
import { useTranslation } from 'react-i18next';
import useServiceTemplateById from 'src/features/one-way-in/hooks/use-service-template-by-id';
import { useUser } from 'src/features/authorization';
import { getServiceDateValidity } from 'src/features/services/utils/service-utils';
import { Divider } from '@digdir/designsystemet-react';
import DocumentTitle from 'src/components/DocumentTitle/DocumentTitle';
import useDocumentTitle from 'src/hooks/use-document-title';

type PageParams = {
  serviceId?: string;
  templateId?: string;
};

function mapOneWayInServiceToUpsertDto(
  service: OneWayInServiceDto | undefined,
  cluster: ClusterDto | undefined,
  user: ClusterUserDto | undefined,
  serviceTemplate: ServiceTemplateDto | undefined,
): OneWayInServiceUpsertDto {
  const baseDefaults: OneWayInServiceUpsertDto = {
    id: undefined,
    clusterId: cluster?.id,
    businessName: cluster?.name,
    businessId: cluster?.id,
    contactName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
    contactEmail: user?.emailAddress,
    contactPhone: user?.phone,
    municipalities: [],
    externalLinks: [],
    keywords: [],
    counties: [],
    companySize: [],
    sponsors: [],
    deliveryTypes: [],
    customerSegment: [],
    selectedEeaArticles: [],
    state: PublicationState.Draft,
    description: undefined,
    customerDescription: undefined,
    name: undefined,
    internalUrl: undefined,
    servicePurpose: undefined,
    applicationDeadline: undefined,
    validFrom: undefined,
    validTo: undefined,
    districtPolicyArea: undefined,
    companyAge: undefined,
    deliveryDescription: undefined,
    hasUserFee: undefined,
    userFeeComments: undefined,
    eeaComments: undefined,
    customerSegmentComments: undefined,
    innovationLevel: undefined,
    categoryCode: undefined,
  };

  // Edit existing service
  if (service) {
    return {
      ...baseDefaults,
      ...service,
      municipalities: service.municipalities || [],
      externalLinks: service.externalLinks || [],
      keywords: service.keywords || [],
      counties: service.counties || [],
      companySize: service.companySize || [],
      sponsors: service.sponsors || [],
      deliveryTypes: service.deliveryTypes || [],
      customerSegment: service.customerSegment || [],
      selectedEeaArticles: service.selectedEeaArticles?.map((eea) => ({ eeaArticleId: eea.id })) || [],
    };
  }

  // Create from template
  if (serviceTemplate) {
    return {
      ...baseDefaults,
      ...serviceTemplate,
      id: undefined,
      keywords: serviceTemplate.keywords || [],
      externalLinks: serviceTemplate.externalLinks || [],
      deliveryTypes: serviceTemplate.deliveryTypes || [],
      sponsors: serviceTemplate.sponsors || [],
      companySize: serviceTemplate.companySizes || [],
      innovationLevel: serviceTemplate.innovationLevel || undefined,
      selectedEeaArticles: serviceTemplate.selectedEeaArticles?.map((id) => ({ eeaArticleId: id })) || [],
    };
  }

  // New service without using template
  return baseDefaults;
}

const defaultClusterUser: ClusterUserDto = {
  id: '',
  clusterId: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
  phone: '22225555',
  responsibilities: '',
  organization: undefined,
  userId: undefined,
  role: undefined,
  b2CId: undefined,
  invitationStatus: undefined,
  invitationCode: undefined,
  opportunityEmailInterval: EmailInterval.Never,
  createdOn: new Date(),
};

const NewServiceForm = () => {
  const [step, setStep] = useState(0);
  const { t: tServices } = useTranslation('services');
  const { t: tPageTitles } = useTranslation('pageTitles');
  const { serviceId, templateId } = useParams<PageParams>();
  const { documentTitle } = useDocumentTitle(
    tServices(serviceId ? 'editService' : 'addService'),
    tPageTitles('services'),
  );
  const { cluster } = useCluster();
  const { service, isInitialLoading: serviceIsLoading } = useOneWayInService(serviceId || '');
  const { displayName, isAadUser } = useUser();
  const { user: clusterUser, isInitialLoading: userIsLoading } = useCurrentClusterUser(isAadUser);

  const user: ClusterUserDto = useMemo(() => {
    return (
      clusterUser ?? {
        ...defaultClusterUser,
        clusterId: cluster?.id || '',
        firstName: displayName ? displayName.split(' ')[0] : '',
        lastName: displayName ? displayName.split(' ').slice(1).join(' ') : '',
        emailAddress: `${displayName ? displayName.split(' ')[0].toLocaleLowerCase() : ''}@test.no`,
      }
    );
  }, [clusterUser, cluster?.id, displayName]);

  const [errors, setErrors] = useState<FieldErrors<Partial<OneWayInServiceUpsertDto>>>({});

  const setManualError: UseFormSetError<OneWayInServiceUpsertDto> = (name, error) => {
    setErrors((prev) => ({
      ...prev,
      [name]: { message: error, type: 'manual' },
    }));
  };

  const { upsertService, deleteService } = useOneWayInServices();

  const { serviceTemplate, isInitialLoading: templateIsLoading } = useServiceTemplateById(templateId);

  const upsertDto = useMemo(() => {
    return mapOneWayInServiceToUpsertDto(service, cluster, user, serviceTemplate);
  }, [service, cluster, user, serviceTemplate]);

  const [formOverrides, setFormOverrides] = useState<Partial<OneWayInServiceUpsertDto>>({});

  const formData = useMemo(() => ({ ...upsertDto, ...formOverrides }), [upsertDto, formOverrides]);

  const purposeRawTextRef = useRef<string>('');
  const eeaCommentsRawTextRef = useRef<string>('');

  const navigate = useNavigate();

  const isValid = () => Object.values(errors).every((error) => !error);
  const dateValidity = getServiceDateValidity(formData.validFrom, formData.validTo);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid()) {
      toast.error(tServices('errors.fillRequired'));
      return;
    }
    const record: OneWayInServiceUpsertDto = {
      ...formData,
      state: PublicationState.Active,
    };
    const promise = upsertService.mutateAsync(record);

    toastPromise(promise, {
      success: tServices(record.id ? 'toasts.published' : 'toasts.created'),
      error: tServices('toasts.error'),
    }).then(() => {
      navigate(`/services/`);
    });
  };

  const onSaveDraft = (data: OneWayInServiceUpsertDto) => {
    if (!data.name) {
      setManualError('name', {
        type: 'manual',
        message: tServices('errors.nameRequired'),
      });
      return;
    }
    const record: OneWayInServiceUpsertDto = {
      ...data,
      state: PublicationState.Draft,
    };
    const promise = upsertService.mutateAsync(record);

    toastPromise(promise, {
      success: tServices('toasts.draftSaved'),
      error: tServices('toasts.error'),
    }).then(() => {
      navigate(`/services/`);
    });
  };

  const onDelete = (data) => {
    if (!data.isDraft && !data.isActive) {
      navigate(`/services/`);
    }
    if (data.isDraft || data.isActive) {
      const promise = deleteService.mutateAsync(data.id);
      toastPromise(promise, {
        success: tServices('toasts.draftDeleted'),
        error: tServices('toasts.error'),
      }).then(() => {
        navigate(`/services/`);
      });
    }
  };

  const steps = [
    { name: tServices('about.title'), link: 'about' },
    { name: tServices('target.title'), link: 'target' },
    { name: tServices('delivery.title'), link: 'delivery' },
    { name: tServices('contact.title'), link: 'contactInfo' },
    { name: tServices('eea.title'), link: 'eea' },
    { name: tServices('summary.title'), link: 'summary' },
  ];

  const updateData = useCallback(<T extends Record<string, any>>(newData: T) => {
    setFormOverrides((prev) => {
      const next = { ...prev, ...newData };
      return JSON.stringify(prev) === JSON.stringify(next) ? prev : next;
    });
  }, []);

  const commonProps: SubFormProps<Partial<OneWayInServiceUpsertDto>> = {
    data: formData,
    updateData,
    errors,
    setErrors,
  };

  if (serviceIsLoading || templateIsLoading || userIsLoading)
    return (
      <div className="display--flex justify-content--center">
        <DocumentTitle title={documentTitle} />
        <Spinner />
      </div>
    );

  const stepComponents = [
    <AboutPage key={0} {...commonProps} purposeRawTextRef={purposeRawTextRef} />,
    <TargetPage key={1} {...commonProps} />,
    <DeliveryPage key={2} {...commonProps} />,
    <ContactInfoPage key={3} {...commonProps} />,
    <EEAPage key={4} {...commonProps} eeaCommentsRawTextRef={eeaCommentsRawTextRef} />,
    <SummaryPage
      key={5}
      {...commonProps}
      purposeRawTextRef={purposeRawTextRef}
      eeaCommentsRawTextRef={eeaCommentsRawTextRef}
    />,
  ];

  return (
    <div className="service-page">
      <DocumentTitle title={documentTitle} />
      <div>
        <FormProgress
          data-size="sm"
          activeStep={step + 1}
          onStepChange={(i) => setStep(i - 1)}
          totalSteps={steps.length}
        >
          {steps.map((stepItem) => (
            <FormProgress.Step key={stepItem.link}>{stepItem.name}</FormProgress.Step>
          ))}
        </FormProgress>
      </div>
      <div className="service-page--form display--flex">
        <div data-size="md">
          <form onSubmit={onSubmit}>
            {stepComponents[step]}
            <div className="display--flex flex-direction--column gap--3 margin-top--3 max-width--100">
              {step === steps.length - 1 &&
                (dateValidity.validFromInFuture || dateValidity.validToExpired) && (
                  <Alert>
                    <div>{tServices('summary.inactiveWarning.title')}</div>
                    <ul>
                      {dateValidity.validFromInFuture && (
                        <li>
                          <em>{tServices('about.validFrom.label')}</em>{' '}
                          {tServices('summary.inactiveWarning.validFrom')}
                        </li>
                      )}
                      {dateValidity.validToExpired && (
                        <li>
                          <em>{tServices('about.validTo.label')}</em>{' '}
                          {tServices('summary.inactiveWarning.validTo')}
                        </li>
                      )}
                    </ul>
                  </Alert>
                )}
              <div className="display--flex flex-direction--row margin-bottom--2 gap--2">
                {step !== 0 && (
                  <Button variant="secondary" type="button" onClick={() => setStep(step - 1)}>
                    <Icon prefix="fass" iconName="arrow-left"></Icon>
                    {tServices('form.back')}
                  </Button>
                )}
                {step < steps.length - 1 && (
                  <Button type="button" onClick={() => setStep(step + 1)}>
                    {tServices('form.next')}
                    <Icon prefix="fass" iconName="arrow-right"></Icon>
                  </Button>
                )}
                {step === steps.length - 1 && (
                  <Button
                    type="submit"
                    disabled={!isValid() || upsertService.isLoading || deleteService.isLoading}
                  >
                    {service?.state === PublicationState.Active
                      ? tServices('form.publishChanges')
                      : tServices('form.publish')}
                  </Button>
                )}
              </div>
              <Divider className="margin--0" />
              <div className="display--flex flex-direction--column">
                <Button
                  type="button"
                  variant="tertiary"
                  disabled={upsertService.isLoading || deleteService.isLoading}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onSaveDraft(formData)}
                >
                  <Icon prefix="fasr" iconName="floppy-disk" />
                  {tServices('form.saveAsDraft')}
                </Button>
                <Button
                  type="button"
                  variant="tertiary"
                  disabled={deleteService.isLoading || upsertService.isLoading}
                  onClick={() => onDelete(formData)}
                >
                  <Icon prefix="fass" iconName="trash-can" />
                  {tServices('form.deleteDraft')}
                </Button>
              </div>
            </div>
          </form>
        </div>
        <div className="service-template-fakedoor"></div>
      </div>
    </div>
  );
};

export default NewServiceForm;
